import { useState, useEffect, useCallback } from "react";
import * as APIModels from "../../../../models";
import {
    SubmissionService,
    DocumentService,
} from "../../../../services/SubmissionService";
import CaseSearch from "../CaseSearch";
import Pleadings from "../Pleadings";
import Fees from "../Fees";
import { authenticationService } from "../../../../services/authentication.service";
import PartialCaseSearch from "../../../efile/components/PartialCaseSearch";

import { useParams } from "react-router-dom";

interface QueryParams {
    submissionId?: string;
    attorneyId?: string;
    filter?: string;
    state?: string;
}

interface Question {
    id: number;
    text: string;
    answered: boolean;
    approved: boolean | null;
}

interface ServiceParty {
    name: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
}

export default function ServiceDocuments({
    submission,
    filter,
}: {
    submission: APIModels.Submission;
    filter?: string;
}) {
    const [completedSubmission, setCompletedSubmission] = useState(submission);
    const [submitting, setSubmitting] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questions, setQuestions] = useState<Question[]>([
        {
            id: 1,
            text: 'Confirm that the summons is the "Official" copy.',
            answered: false,
            approved: null,
        },
        {
            id: 2,
            text: "Confirm that the summons has a signature from the clerk.",
            answered: false,
            approved: null,
        },
    ]);
    const [userName, setUserName] = useState<string>("");
    const [caseSearchCompleted, setCaseSearchCompleted] = useState(false);
    const [selectedCaseResult, setSelectedCaseResult] =
        useState<APIModels.Submission | null>(null);
    const [serviceParty, setServiceParty] = useState<ServiceParty>({
        name: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
    });
    const [selectedPartyFromCase, setSelectedPartyFromCase] =
        useState<string>("");
    const [showOriginalSuitLookup, setShowOriginalSuitLookup] =
        useState<boolean>(true); // Start expanded by default
    const [searchAttempted, setSearchAttempted] = useState<boolean>(false);
    const [skippedOriginalSuit, setSkippedOriginalSuit] =
        useState<boolean>(false);
    const [includedOriginalDocIds, setIncludedOriginalDocIds] = useState<
        Set<string>
    >(new Set());

    // Check if submission has only one document
    const hasSingleDocument =
        completedSubmission?.documents?.length === 1 ||
        completedSubmission?.documentCount === 1;

    // Check if the single document contains 'summon'
    const hasSummonDocument =
        hasSingleDocument &&
        completedSubmission?.documents?.[0] &&
        ((completedSubmission.documents[0].filename || "")
            .toLowerCase()
            .includes("summon") ||
            (completedSubmission.documents[0].documentType || "")
                .toLowerCase()
                .includes("summon"));

    // Only show case search if it's a single document that contains 'summon'
    const showCaseSearch = hasSummonDocument;

    const params: QueryParams = useParams<{
        submissionId?: string;
        attorneyId?: string;
        filter?: string;
        state?: string;
    }>();

    useEffect(() => {
        setCompletedSubmission(submission);

        // Get current user's name
        const currentUser = authenticationService.currentUserValue;
        if (currentUser) {
            const fullName = `${currentUser.firstName || ""} ${
                currentUser.lastName || ""
            }`.trim();
            setUserName(fullName || currentUser.username || "User");
        }

        // If submission already has a party, populate the service party
        if (submission.parties && submission.parties.length > 0) {
            const firstParty = submission.parties[0];
            setServiceParty({
                name:
                    firstParty.name ||
                    `${firstParty.firstName || ""} ${
                        firstParty.middleName || ""
                    } ${firstParty.lastName || ""}`.trim() ||
                    "",
                addressLine1: firstParty.address?.streetAddress || "",
                addressLine2: firstParty.address?.unit || "",
                city: firstParty.address?.city || "",
                state: firstParty.address?.state || "",
                zipCode: firstParty.address?.zip || "",
            });
        }
    }, [submission]);

    const handleApprove = () => {
        const updatedQuestions = [...questions];
        updatedQuestions[currentQuestionIndex] = {
            ...updatedQuestions[currentQuestionIndex],
            answered: true,
            approved: true,
        };
        setQuestions(updatedQuestions);

        // Move to next question if available
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handleCaseSelected = useCallback((result: APIModels.Submission) => {
        // Update the completedSubmission with the CaseData from the search result
        setCompletedSubmission((prev) => ({
            ...prev,
            caseData: result.caseData,
        }));
        setSelectedCaseResult(result);
        setCaseSearchCompleted(true);
        setShowOriginalSuitLookup(false);
        setSearchAttempted(true); // Mark that a search has been performed
        setSkippedOriginalSuit(false); // Reset skip flag when a case is selected

        // Default none of the original documents to included
        setIncludedOriginalDocIds(new Set());
    }, []);

    const handleCaseSearchComplete = useCallback(() => {
        setSearchAttempted(true); // Mark that a search has been performed
    }, []);

    const toggleOriginalDoc = (docId: string) => {
        setIncludedOriginalDocIds((prev) => {
            const next = new Set(prev);
            if (next.has(docId)) {
                next.delete(docId);
            } else {
                next.add(docId);
            }
            return next;
        });
    };

    const handlePartySelect = (partyId: string) => {
        setSelectedPartyFromCase(partyId);
        if (selectedCaseResult?.parties) {
            const selectedParty = selectedCaseResult.parties.find(
                (p) => p.id === partyId
            );
            if (selectedParty) {
                const partyName =
                    selectedParty.name ||
                    `${selectedParty.firstName || ""} ${
                        selectedParty.lastName || ""
                    }`.trim() ||
                    "";
                setServiceParty({
                    name: partyName,
                    addressLine1: selectedParty.address?.streetAddress || "",
                    addressLine2: selectedParty.address?.unit || "",
                    city: selectedParty.address?.city || "",
                    state: selectedParty.address?.state || "",
                    zipCode: selectedParty.address?.zip || "",
                });
            }
        }
    };

    const handleClearParty = () => {
        setServiceParty({
            name: "",
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zipCode: "",
        });
        setSelectedPartyFromCase("");
    };

    const handleSkipOriginalSuit = () => {
        setSkippedOriginalSuit(true);
        setShowOriginalSuitLookup(false);
    };

    const handleReject = useCallback(() => {
        const updatedQuestions = [...questions];
        updatedQuestions[currentQuestionIndex] = {
            ...updatedQuestions[currentQuestionIndex],
            answered: true,
            approved: false,
        };
        setQuestions(updatedQuestions);

        // TODO: Add custom logic for rejection handling
        console.log(
            `Question ${currentQuestionIndex + 1} rejected:`,
            questions[currentQuestionIndex].text
        );

        SubmissionService.fail({
            submissionId: completedSubmission.id ?? "",
            body: {
                defaultMessage: 9,
                message: `Question ${currentQuestionIndex + 1} rejected: ${
                    questions[currentQuestionIndex].text
                }`,
            },
        }).then(
            (resp) => {
                window.location.href = "/queue/" + filter;
            },
            (error) => {
                console.log(error);
                //setError(error);
            }
        );
    }, [completedSubmission]);

    const allQuestionsAnswered = questions.every((q) => q.answered);
    const allQuestionsApproved = questions.every((q) => q.approved === true);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Check if case search is required and completed
        if (showCaseSearch && !caseSearchCompleted) {
            alert(
                "Please search for and select a matching case before submitting."
            );
            return;
        }

        // Check if original suit is selected or skipped
        if (!selectedCaseResult && !skippedOriginalSuit) {
            alert(
                "Please search for and select the original suit, or click Skip after attempting a search."
            );
            return;
        }

        // Check if party information is filled out
        if (
            !serviceParty.name.trim() ||
            !serviceParty.addressLine1.trim() ||
            !serviceParty.city.trim() ||
            !serviceParty.state.trim() ||
            !serviceParty.zipCode.trim()
        ) {
            alert(
                "Please fill out all required party information (Name, Address Line 1, City, State, ZIP Code)."
            );
            return;
        }

        if (!allQuestionsAnswered || !allQuestionsApproved) {
            alert("Please answer and approve all questions before submitting.");
            return;
        }

        // Gather the selected original documents to include
        const selectedOriginalDocs = (
            selectedCaseResult?.documents || []
        ).filter((doc) => doc.id && includedOriginalDocIds.has(doc.id));

        setSubmitting(true);
        SubmissionService.serve({
            attorneyId: completedSubmission.attorneyID,
            filter: filter,
            state: completedSubmission.portalIdentifier,
            body: {
                originalNewCaseSubmissionID: selectedCaseResult?.id,
                serviceSubmission: {
                    ...completedSubmission,
                    documents: [
                        ...(completedSubmission.documents || []),
                        ...selectedOriginalDocs,
                    ],
                    parties: [
                        // Include all parties from the original suit
                        ...(selectedCaseResult?.parties || []),
                        // Add the service party with specific party type
                        {
                            name: serviceParty.name,
                            partyType: "SERVICE PARTY",
                            address: {
                                streetAddress: serviceParty.addressLine1,
                                unit: serviceParty.addressLine2,
                                city: serviceParty.city,
                                state: serviceParty.state,
                                zip: serviceParty.zipCode,
                            },
                        },
                    ],
                },
            },
        }).then(
            (resp) => {
                console.log(resp);
                if (resp.data && resp.data.id) {
                    //show efile success
                    //setNextSubmission(resp.data);
                    window.location.href = `/efile?submissionId=${resp.data.id}&filter=${filter}`;
                } else {
                    //TODO: Show error
                    window.location.href = "/queue/" + filter;
                }
            },
            (error) => {
                setSubmitting(false);
                window.location.href = "/queue/" + filter;
            }
        );
    };

    return (
        <>
            <form className="needs-validation" onSubmit={onSubmit}>
                <div className="row">
                    <div className="col-12 mb-4">
                        {/* Questions Section - Show First */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <h4 className="card-title mb-4">
                                    Service Document Verification
                                </h4>

                                {/* Progress indicator */}
                                <div className="mb-4">
                                    <div className="progress">
                                        <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{
                                                width: `${
                                                    (questions.filter(
                                                        (q) => q.answered
                                                    ).length /
                                                        questions.length) *
                                                    100
                                                }%`,
                                            }}
                                            aria-valuenow={
                                                questions.filter(
                                                    (q) => q.answered
                                                ).length
                                            }
                                            aria-valuemin={0}
                                            aria-valuemax={questions.length}
                                        >
                                            {
                                                questions.filter(
                                                    (q) => q.answered
                                                ).length
                                            }{" "}
                                            of {questions.length} completed
                                        </div>
                                    </div>
                                </div>

                                {/* Question display */}
                                {currentQuestionIndex < questions.length &&
                                    !questions[currentQuestionIndex]
                                        .answered && (
                                        <div className="question-container">
                                            <div className="alert alert-info mb-3">
                                                <h5 className="mb-3">
                                                    Question{" "}
                                                    {currentQuestionIndex + 1}{" "}
                                                    of {questions.length}
                                                </h5>
                                                <p
                                                    className="mb-3"
                                                    style={{
                                                        fontSize: "1.1rem",
                                                    }}
                                                >
                                                    I,{" "}
                                                    <strong>{userName}</strong>,{" "}
                                                    {
                                                        questions[
                                                            currentQuestionIndex
                                                        ].text
                                                    }
                                                    :
                                                </p>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-success"
                                                        onClick={handleApprove}
                                                    >
                                                        <i className="fas fa-check me-2"></i>
                                                        Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={handleReject}
                                                    >
                                                        <i className="fas fa-times me-2"></i>
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {/* Summary of answered questions */}
                                {questions.filter((q) => q.answered).length >
                                    0 && (
                                    <div className="mt-4">
                                        <h5>Answered Questions:</h5>
                                        <ul className="list-group">
                                            {questions
                                                .filter((q) => q.answered)
                                                .map((question, index) => (
                                                    <li
                                                        key={question.id}
                                                        className={`list-group-item d-flex justify-content-between align-items-center ${
                                                            question.approved
                                                                ? "list-group-item-success"
                                                                : "list-group-item-danger"
                                                        }`}
                                                    >
                                                        <span>
                                                            {index + 1}.{" "}
                                                            {question.text}
                                                        </span>
                                                        <span
                                                            className={`badge ${
                                                                question.approved
                                                                    ? "bg-success"
                                                                    : "bg-danger"
                                                            }`}
                                                        >
                                                            {question.approved
                                                                ? "Confirm"
                                                                : "Rejected"}
                                                        </span>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Completion message */}
                                {allQuestionsAnswered && (
                                    <div
                                        className={`alert ${
                                            allQuestionsApproved
                                                ? "alert-success"
                                                : "alert-warning"
                                        } mt-4`}
                                    >
                                        {allQuestionsApproved ? (
                                            <>
                                                <i className="fas fa-check-circle me-2"></i>
                                                All questions have been
                                                approved. You may now proceed to
                                                select the original suit.
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                Some questions were rejected.
                                                Please review before proceeding.
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Case Search Section - Show in accordion if submission has single summon document */}
                        {showCaseSearch && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h4 className="card-title mb-4">
                                        Case Search{" "}
                                        {caseSearchCompleted
                                            ? "✓"
                                            : "(Required)"}
                                    </h4>

                                    {!caseSearchCompleted && (
                                        <div className="alert alert-warning mb-3">
                                            <i className="fas fa-exclamation-triangle me-2"></i>
                                            This submission contains a summons
                                            document. Please search for and
                                            select the matching case before
                                            proceeding.
                                        </div>
                                    )}

                                    {caseSearchCompleted && (
                                        <div className="alert alert-success mb-3">
                                            <i className="fas fa-check-circle me-2"></i>
                                            Case selected:{" "}
                                            <strong>
                                                {
                                                    selectedCaseResult?.caseData
                                                        ?.ucn
                                                }
                                            </strong>{" "}
                                            -{" "}
                                            {
                                                selectedCaseResult?.caseData
                                                    ?.caseTitle
                                            }
                                        </div>
                                    )}

                                    <div
                                        className="accordion"
                                        id="caseSearchAccordion"
                                    >
                                        <div className="accordion-item">
                                            <h2
                                                className="accordion-header"
                                                id="caseSearchHeading"
                                            >
                                                <button
                                                    className={`accordion-button ${
                                                        caseSearchCompleted
                                                            ? "collapsed"
                                                            : ""
                                                    }`}
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#caseSearchCollapse"
                                                    aria-expanded={
                                                        !caseSearchCompleted
                                                    }
                                                    aria-controls="caseSearchCollapse"
                                                >
                                                    {caseSearchCompleted
                                                        ? "Change Selected Case"
                                                        : "Search for Case"}
                                                </button>
                                            </h2>
                                            <div
                                                id="caseSearchCollapse"
                                                className={`accordion-collapse collapse ${
                                                    !caseSearchCompleted
                                                        ? "show"
                                                        : ""
                                                }`}
                                                aria-labelledby="caseSearchHeading"
                                                data-bs-parent="#caseSearchAccordion"
                                            >
                                                <div className="accordion-body">
                                                    <PartialCaseSearch
                                                        sequenceDetected={
                                                            completedSubmission
                                                                .caseData
                                                                ?.courtCaseNumber ||
                                                            ""
                                                        }
                                                        searchingAttorneyId={
                                                            completedSubmission?.attorneyID
                                                        }
                                                        onSearchComplete={
                                                            handleCaseSearchComplete
                                                        }
                                                        onCaseSelected={
                                                            handleCaseSelected
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Original Suit Lookup Section - Always show after case search */}
                        {(!showCaseSearch || caseSearchCompleted) && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-0">
                                            Original Suit Lookup{" "}
                                            {!selectedCaseResult &&
                                                !skippedOriginalSuit && (
                                                    <span className="text-danger">
                                                        (Required)
                                                    </span>
                                                )}
                                            {selectedCaseResult && (
                                                <span className="text-success">
                                                    ✓
                                                </span>
                                            )}
                                            {skippedOriginalSuit && (
                                                <span className="text-muted">
                                                    (Skipped)
                                                </span>
                                            )}
                                        </h4>
                                        {!selectedCaseResult &&
                                            !skippedOriginalSuit && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={
                                                        handleSkipOriginalSuit
                                                    }
                                                    disabled={!searchAttempted}
                                                    title={
                                                        !searchAttempted
                                                            ? "Please attempt a search first"
                                                            : "Skip original suit lookup"
                                                    }
                                                >
                                                    Skip
                                                </button>
                                            )}
                                        {skippedOriginalSuit && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => {
                                                    setSkippedOriginalSuit(
                                                        false
                                                    );
                                                    setShowOriginalSuitLookup(
                                                        true
                                                    );
                                                }}
                                                title="Search for original suit"
                                            >
                                                Search for Case
                                            </button>
                                        )}
                                    </div>

                                    {!selectedCaseResult &&
                                        !skippedOriginalSuit && (
                                            <div className="alert alert-warning mb-3">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                Please search for and select the
                                                original suit before proceeding,
                                                or click Skip after attempting a
                                                search.
                                            </div>
                                        )}
                                    {skippedOriginalSuit && (
                                        <div className="alert alert-info mb-3">
                                            <i className="fas fa-info-circle me-2"></i>
                                            Original suit lookup has been
                                            skipped. You may proceed without
                                            selecting a case.
                                        </div>
                                    )}

                                    {!skippedOriginalSuit && (
                                        <div
                                            className="accordion"
                                            id="originalSuitAccordion"
                                        >
                                            <div className="accordion-item">
                                                <h2
                                                    className="accordion-header"
                                                    id="originalSuitHeading"
                                                >
                                                    <button
                                                        className={`accordion-button ${
                                                            !showOriginalSuitLookup
                                                                ? "collapsed"
                                                                : ""
                                                        }`}
                                                        type="button"
                                                        data-bs-toggle="collapse"
                                                        data-bs-target="#originalSuitCollapse"
                                                        aria-expanded={
                                                            showOriginalSuitLookup
                                                        }
                                                        aria-controls="originalSuitCollapse"
                                                        onClick={() =>
                                                            setShowOriginalSuitLookup(
                                                                !showOriginalSuitLookup
                                                            )
                                                        }
                                                    >
                                                        {selectedCaseResult
                                                            ? "Change Original Suit"
                                                            : "Search for Original Suit"}
                                                    </button>
                                                </h2>
                                                <div
                                                    id="originalSuitCollapse"
                                                    className={`accordion-collapse collapse ${
                                                        showOriginalSuitLookup ||
                                                        !selectedCaseResult
                                                            ? "show"
                                                            : ""
                                                    }`}
                                                    aria-labelledby="originalSuitHeading"
                                                    data-bs-parent="#originalSuitAccordion"
                                                >
                                                    <div className="accordion-body">
                                                        <PartialCaseSearch
                                                            sequenceDetected={
                                                                completedSubmission
                                                                    .caseData
                                                                    ?.courtCaseNumber ||
                                                                ""
                                                            }
                                                            searchingAttorneyId={
                                                                completedSubmission?.attorneyID
                                                            }
                                                            onSearchComplete={
                                                                handleCaseSearchComplete
                                                            }
                                                            onCaseSelected={
                                                                handleCaseSelected
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedCaseResult && (
                                        <div className="alert alert-success mt-3">
                                            <i className="fas fa-check-circle me-2"></i>
                                            Original suit selected:{" "}
                                            <strong>
                                                {
                                                    selectedCaseResult?.caseData
                                                        ?.ucn
                                                }
                                            </strong>{" "}
                                            -{" "}
                                            {
                                                selectedCaseResult?.caseData
                                                    ?.caseTitle
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Service Party Information Section - Only show after original suit is selected or skipped */}
                        {(!showCaseSearch || caseSearchCompleted) &&
                            (selectedCaseResult || skippedOriginalSuit) && (
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h4 className="card-title mb-4">
                                            Person Being Served
                                        </h4>

                                        {/* Party Selection from Original Case */}
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Select Party from Original Suit
                                                (Optional)
                                            </label>
                                            <div className="input-group">
                                                <select
                                                    className="form-select"
                                                    value={
                                                        selectedPartyFromCase
                                                    }
                                                    onChange={(e) =>
                                                        handlePartySelect(
                                                            e.target.value
                                                        )
                                                    }
                                                    disabled={
                                                        !selectedCaseResult?.parties ||
                                                        selectedCaseResult
                                                            .parties.length ===
                                                            0
                                                    }
                                                >
                                                    <option value="">
                                                        {selectedCaseResult?.parties &&
                                                        selectedCaseResult
                                                            .parties.length > 0
                                                            ? "-- Select a party --"
                                                            : "-- No original suit selected --"}
                                                    </option>
                                                    {selectedCaseResult?.parties &&
                                                        selectedCaseResult.parties.map(
                                                            (party, index) => {
                                                                const displayName =
                                                                    party.name ||
                                                                    `${
                                                                        party.firstName ||
                                                                        ""
                                                                    } ${
                                                                        party.lastName ||
                                                                        ""
                                                                    }`.trim() ||
                                                                    `Party ${
                                                                        index +
                                                                        1
                                                                    }`;
                                                                return (
                                                                    <option
                                                                        key={
                                                                            party.id ||
                                                                            index
                                                                        }
                                                                        value={
                                                                            party.id ||
                                                                            index
                                                                        }
                                                                    >
                                                                        {
                                                                            displayName
                                                                        }
                                                                    </option>
                                                                );
                                                            }
                                                        )}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() =>
                                                        setShowOriginalSuitLookup(
                                                            true
                                                        )
                                                    }
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#originalSuitCollapse"
                                                    aria-expanded="false"
                                                    aria-controls="originalSuitCollapse"
                                                >
                                                    <i className="fas fa-search me-2"></i>
                                                    {selectedCaseResult
                                                        ? "Change Suit"
                                                        : "Find Suit"}
                                                </button>
                                            </div>
                                            {selectedCaseResult && (
                                                <small className="form-text text-muted">
                                                    Original suit:{" "}
                                                    <strong>
                                                        {
                                                            selectedCaseResult
                                                                ?.caseData?.ucn
                                                        }
                                                    </strong>{" "}
                                                    -{" "}
                                                    {
                                                        selectedCaseResult
                                                            ?.caseData
                                                            ?.caseTitle
                                                    }
                                                </small>
                                            )}
                                        </div>

                                        <div className="row">
                                            <div className="col-12 mb-3">
                                                <label className="form-label">
                                                    <strong>
                                                        Name of Person Being
                                                        Served *
                                                    </strong>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={serviceParty.name}
                                                    onChange={(e) =>
                                                        setServiceParty(
                                                            (prev) => ({
                                                                ...prev,
                                                                name: e.target
                                                                    .value,
                                                            })
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-12 mb-2">
                                                <label className="form-label">
                                                    <strong>
                                                        Service Address
                                                    </strong>
                                                </label>
                                            </div>
                                            <div className="col-12 mb-3">
                                                <label className="form-label">
                                                    Address Line 1 *
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={
                                                        serviceParty.addressLine1
                                                    }
                                                    onChange={(e) =>
                                                        setServiceParty(
                                                            (prev) => ({
                                                                ...prev,
                                                                addressLine1:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 mb-3">
                                                <label className="form-label">
                                                    Address Line 2
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={
                                                        serviceParty.addressLine2
                                                    }
                                                    onChange={(e) =>
                                                        setServiceParty(
                                                            (prev) => ({
                                                                ...prev,
                                                                addressLine2:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">
                                                    City *
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={serviceParty.city}
                                                    onChange={(e) =>
                                                        setServiceParty(
                                                            (prev) => ({
                                                                ...prev,
                                                                city: e.target
                                                                    .value,
                                                            })
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-3 mb-3">
                                                <label className="form-label">
                                                    State *
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={serviceParty.state}
                                                    onChange={(e) =>
                                                        setServiceParty(
                                                            (prev) => ({
                                                                ...prev,
                                                                state: e.target
                                                                    .value,
                                                            })
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-3 mb-3">
                                                <label className="form-label">
                                                    ZIP Code *
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={serviceParty.zipCode}
                                                    onChange={(e) =>
                                                        setServiceParty(
                                                            (prev) => ({
                                                                ...prev,
                                                                zipCode:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        )
                                                    }
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-12">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={handleClearParty}
                                                >
                                                    <i className="fas fa-eraser me-2"></i>
                                                    Clear Fields
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        {/* Current Submission Documents */}
                        {completedSubmission?.documents &&
                            completedSubmission.documents.length > 0 && (
                                <div className="card mt-4">
                                    <div className="card-body">
                                        <h5 className="card-title mb-3">
                                            Documents in This Submission
                                        </h5>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Document Name</th>
                                                        <th>Document Type</th>
                                                        <th>Pages</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {completedSubmission.documents.map(
                                                        (doc, index) => (
                                                            <tr
                                                                key={`existing-${
                                                                    doc.id ||
                                                                    index
                                                                }`}
                                                            >
                                                                <td>
                                                                    {index + 1}
                                                                </td>
                                                                <td>
                                                                    {doc.filename ||
                                                                        "N/A"}
                                                                </td>
                                                                <td>
                                                                    {doc.documentType ||
                                                                        "N/A"}
                                                                </td>
                                                                <td>
                                                                    {doc.pageCount ||
                                                                        "N/A"}
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                        {/* Original Case Documents with Include Toggles */}
                        {selectedCaseResult?.documents &&
                            selectedCaseResult.documents.length > 0 && (
                                <div className="card mt-4">
                                    <div className="card-body">
                                        <h5 className="card-title mb-3">
                                            Original Case Documents
                                            <small className="text-muted ms-2">
                                                (
                                                {
                                                    selectedCaseResult.caseData
                                                        ?.ucn
                                                }{" "}
                                                -{" "}
                                                {
                                                    selectedCaseResult.caseData
                                                        ?.caseTitle
                                                }
                                                )
                                            </small>
                                        </h5>
                                        <p className="text-muted mb-3">
                                            Toggle which documents from the
                                            original case to include in the
                                            service package.
                                        </p>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th
                                                            style={{
                                                                width: "60px",
                                                            }}
                                                        >
                                                            Include
                                                        </th>
                                                        <th>#</th>
                                                        <th>Document Name</th>
                                                        <th>Document Type</th>
                                                        <th>Pages</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedCaseResult.documents.map(
                                                        (doc, index) => (
                                                            <tr
                                                                key={`original-${
                                                                    doc.id ||
                                                                    index
                                                                }`}
                                                                className={
                                                                    doc.id &&
                                                                    includedOriginalDocIds.has(
                                                                        doc.id
                                                                    )
                                                                        ? ""
                                                                        : "table-secondary"
                                                                }
                                                            >
                                                                <td className="text-center">
                                                                    <div className="form-check form-switch d-flex justify-content-center mb-0">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            role="switch"
                                                                            id={`include-doc-${
                                                                                doc.id ||
                                                                                index
                                                                            }`}
                                                                            checked={
                                                                                !!doc.id &&
                                                                                includedOriginalDocIds.has(
                                                                                    doc.id
                                                                                )
                                                                            }
                                                                            onChange={() =>
                                                                                doc.id &&
                                                                                toggleOriginalDoc(
                                                                                    doc.id
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                !doc.id
                                                                            }
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {index + 1}
                                                                </td>
                                                                <td>
                                                                    {doc.filename ||
                                                                        "N/A"}
                                                                </td>
                                                                <td>
                                                                    {doc.documentType ||
                                                                        "N/A"}
                                                                </td>
                                                                <td>
                                                                    {doc.pageCount ||
                                                                        "N/A"}
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                            <small className="text-muted">
                                                {includedOriginalDocIds.size} of{" "}
                                                {
                                                    selectedCaseResult.documents
                                                        .length
                                                }{" "}
                                                documents selected
                                            </small>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm me-2"
                                                    onClick={() =>
                                                        setIncludedOriginalDocIds(
                                                            new Set()
                                                        )
                                                    }
                                                >
                                                    Deselect All
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() =>
                                                        setIncludedOriginalDocIds(
                                                            new Set(
                                                                selectedCaseResult.documents
                                                                    ?.map(
                                                                        (d) =>
                                                                            d.id
                                                                    )
                                                                    .filter(
                                                                        (
                                                                            id
                                                                        ): id is string =>
                                                                            !!id
                                                                    ) || []
                                                            )
                                                        )
                                                    }
                                                >
                                                    Select All
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>

                <div className="row">
                    <div className="col-12" style={{ textAlign: "center" }}>
                        {submitting ? (
                            <button
                                className="btn btn-primary"
                                type="button"
                                disabled={true}
                            >
                                <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Submitting...
                            </button>
                        ) : (
                            <button
                                className="btn btn-primary"
                                type="submit"
                                disabled={
                                    (showCaseSearch && !caseSearchCompleted) ||
                                    (!selectedCaseResult &&
                                        !skippedOriginalSuit) ||
                                    !serviceParty.name.trim() ||
                                    !serviceParty.addressLine1.trim() ||
                                    !serviceParty.city.trim() ||
                                    !serviceParty.state.trim() ||
                                    !serviceParty.zipCode.trim() ||
                                    !allQuestionsAnswered ||
                                    !allQuestionsApproved
                                }
                            >
                                Save
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </>
    );
}
